import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getCookie } from "../utils/cookie";
import {
  getRefreshToken,
  clearAuthCookies,
  hasRefreshableSession,
  isGuestRoute,
  resetDataAndLogout,
} from "./session";
import { decodeToken } from "../utils/jwt";
import { logout } from "../redux/slices/auth/authSlice";
import { useToast } from "../contexts/ToastContext";

const WARNING_BEFORE_MS = 300 * 1000;
const CHECK_INTERVAL_MS = 1 * 1000;
const MIN_REFRESH_GAP_MS = 5 * 1000;
// Phase 7: officers auto-logout after IDLE_THRESHOLD_MS without activity.
const IDLE_THRESHOLD_MS = 30 * 60 * 1000;
const ACTIVITY_REFRESH_WINDOW_MS = 120 * 1000;
const ACTIVITY_EVENTS = [
  "click",
  "dblclick",
  "contextmenu",
  "keydown",
  "keypress",
  "mousemove",
  "mousedown",
  "touchstart",
  "scroll",
];

const SessionManager = ({ children }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const refreshingRef = useRef(false);
  const warnedRef = useRef(false);
  const lastAttemptRef = useRef(0);
  const consecutiveFailuresRef = useRef(0);

  const tryRefresh = useCallback(async () => {
    if (refreshingRef.current) {
      return getRefreshToken();
    }
    refreshingRef.current = true;
    try {
      const newToken = await getRefreshToken();
      if (newToken) {
        warnedRef.current = false;
        setShowWarning(false);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  const attemptRefresh = useCallback(
    async ({ force = false } = {}) => {
      const now = Date.now();
      const failures = consecutiveFailuresRef.current;
      const gap = Math.min(
        MIN_REFRESH_GAP_MS * Math.pow(2, failures),
        60 * 1000
      );
      if (!force && now - lastAttemptRef.current < gap) {
        return null;
      }
      lastAttemptRef.current = now;
      const result = await tryRefresh();
      if (result) {
        consecutiveFailuresRef.current = 0;
      } else {
        consecutiveFailuresRef.current = Math.min(failures + 1, 10);
      }
      return result;
    },
    [tryRefresh]
  );

  useEffect(() => {
    let lastWrite = 0;
    let lastProbe = 0;

    const maybeProactiveRefresh = async () => {
      if (!hasRefreshableSession()) return;

      const accessToken = getCookie("accessToken");
      let remaining = Infinity;
      if (!accessToken) {
        remaining = -1;
      } else {
        const decoded = decodeToken(accessToken);
        if (decoded?.exp) remaining = decoded.exp * 1000 - Date.now();
      }

      if (remaining <= ACTIVITY_REFRESH_WINDOW_MS) {
        await attemptRefresh();
      }
    };

    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastWrite > 1000) {
        lastWrite = now;
        localStorage.setItem("lastActivity", now.toString());
      }
      if (now - lastProbe > 2000) {
        lastProbe = now;
        maybeProactiveRefresh();
      }
    };

    ACTIVITY_EVENTS.forEach((e) =>
      document.addEventListener(e, updateActivity, {
        capture: true,
        passive: true,
      })
    );

    return () => {
      ACTIVITY_EVENTS.forEach((e) =>
        document.removeEventListener(e, updateActivity, { capture: true })
      );
    };
  }, [attemptRefresh]);

  useEffect(() => {
    const check = async () => {
      // Room/landing paths hide the banner, but an authenticated officer's
      // login token must still rotate silently inside a room (Phase 6), so the
      // session cookie itself decides whether any of this runs — not the path.
      const hideUi = isGuestRoute();
      const accessToken = getCookie("accessToken");
      const hasSession = Boolean(
        getCookie("refreshExpiresIn") || getCookie("refreshToken"),
      );

      // Phase 7 idle logout. Tracked via the activity events below; the idle
      // clock resets while inside a room, so long calls (and the landing /
      // login pages) never count toward the 30-minute threshold.
      if (hideUi && hasSession) {
        lastActivityRef.current = Date.now();
      }
      const idleTimeMs = Date.now() - lastActivityRef.current;
      if (hasSession && !hideUi && idleTimeMs >= IDLE_THRESHOLD_MS) {
        clearAuthCookies();
        dispatch(logout());
        resetDataAndLogout();
        return;
      }

      if (!accessToken) {
        if (!hasSession) {
          setShowWarning(false);
          warnedRef.current = false;
          return;
        }
        if (refreshingRef.current) return;
        const refreshed = await attemptRefresh();
        if (!refreshed) {
          setShowWarning(false);
          warnedRef.current = false;
        }
        return;
      }

      try {
        const decoded = decodeToken(accessToken);
        if (!decoded || !decoded.exp) return;

        const timeRemaining = decoded.exp * 1000 - Date.now();

        if (timeRemaining <= 0) {
          if (refreshingRef.current) return;
          const refreshed = await attemptRefresh();
          if (!refreshed) {
            clearAuthCookies();
            dispatch(logout());
          }
          return;
        }

        if (timeRemaining <= WARNING_BEFORE_MS) {
          if (!hideUi) {
            setTimeLeft(Math.ceil(timeRemaining / 1000));
            setShowWarning(true);
          }

          if (!refreshingRef.current) {
            await attemptRefresh();
          }
        } else {
          setShowWarning(false);
          warnedRef.current = false;
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [attemptRefresh, dispatch]);

  const handleStay = async () => {
    lastActivityRef.current = Date.now();
    localStorage.setItem("lastActivity", Date.now().toString());
    const newToken = await attemptRefresh({ force: true });
    if (newToken) {
      setShowWarning(false);
      toast?.success?.("Session extended successfully");
    } else {
      toast?.error?.("Could not extend session. Please sign in again.");
    }
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      {children}
      {showWarning && (
        <div
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            zIndex: 9999,
            backgroundColor: "#1e293b",
            color: "#f1f5f9",
            padding: "14px 20px",
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "0.85rem",
            maxWidth: "400px",
            animation: "sessionSlideIn 0.3s ease-out",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#facc15"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: "2px" }}>
              Session expiring in {timeLeft}s
            </div>
            <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
              Click anywhere or press a key to stay logged in
            </div>
          </div>
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleStay}
            style={{
              background: "#2563eb",
              border: "none",
              color: "#ffffff",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Stay
          </button>
        </div>
      )}
      <style>{`@keyframes sessionSlideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </div>
  );
};

export default SessionManager;
